import { useMemo, useState } from "react";
import { AdvertisementsContent } from "content";
import {
  useAdminAdvertisements,
  useCreateAdvertisement,
  useDeleteAdvertisement,
} from "hooks/api/advertisements";
import { useModal } from "hooks/common";
import { Space } from "components/Common/Space";
import { Divider } from "components/Common/Divider";
import { Empty } from "components/Common/Empty";
import { ContentModal } from "components/Common/ContentModal";
import { ActionModal } from "components/Common/ActionModal";
import { Button } from "components/Interactive/Button";
import { ImageUploadModal } from "components/Interactive/ImageUploadModal";
import { Paragraph } from "components/Typography/Paragraph";
import { logError, logSuccess } from "utils/notifications";
import { convertBlobToBase64String } from "utils/common";
import {
  EAdvertisementTargetRole,
  TAdvertisement,
} from "types/api/advertisement";
import "./Advertisements.styles.scss";

const TARGET_ROLE_OPTIONS = [
  { value: null, label: AdvertisementsContent.Add.TargetRole.ALL },
  {
    value: EAdvertisementTargetRole.TEACHER,
    label: AdvertisementsContent.Add.TargetRole.TEACHER,
  },
  {
    value: EAdvertisementTargetRole.STUDENT,
    label: AdvertisementsContent.Add.TargetRole.STUDENT,
  },
];

const TARGET_ROLE_LABEL: Record<string, string> = {
  [EAdvertisementTargetRole.TEACHER]:
    AdvertisementsContent.Add.TargetRole.TEACHER,
  [EAdvertisementTargetRole.STUDENT]:
    AdvertisementsContent.Add.TargetRole.STUDENT,
};

export const AdvertisementsDashboard: React.FC = () => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [adUrl, setAdUrl] = useState<string>("");
  const [targetRole, setTargetRole] = useState<EAdvertisementTargetRole | null>(
    null
  );
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal(false);
  const {
    isModalOpen: isImageModalOpen,
    openModal: openImageModal,
    closeModal: closeImageModal,
  } = useModal(false);
  const {
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal(false);

  const { data: advertisements = [], isFetching } = useAdminAdvertisements();
  const { mutate: createAd, isLoading: isCreating } = useCreateAdvertisement();
  const { mutate: deleteAd } = useDeleteAdvertisement();

  const isEmpty = useMemo(() => advertisements.length === 0, [advertisements]);

  const imageUploadHandler = (image: Blob) => {
    convertBlobToBase64String(image).then((base64) => {
      setUploadedImage(base64);
      closeImageModal();
    });
  };

  const addSubmitHandler = () => {
    if (!uploadedImage) {
      logError({ message: AdvertisementsContent.Notification.Add.ERROR });
      return;
    }
    const [, cleanBase64] = uploadedImage.split(",");
    createAd(
      { imageEncoded: cleanBase64, url: adUrl.trim() || null, targetRole },
      {
        onSuccess: () => {
          logSuccess(AdvertisementsContent.Notification.Add.SUCCESS);
          setUploadedImage(null);
          setAdUrl("");
          setTargetRole(null);
          closeAddModal();
        },
      }
    );
  };

  const closeAddModalHandler = () => {
    setUploadedImage(null);
    setAdUrl("");
    setTargetRole(null);
    closeAddModal();
  };

  const deleteClickHandler = (ad: TAdvertisement) => {
    setSelectedAdId(ad.id);
    openDeleteModal();
  };

  const deleteSubmitHandler = () => {
    if (selectedAdId === null) {
      logError({ message: AdvertisementsContent.Delete.Notification.ERROR });
      return;
    }
    deleteAd(selectedAdId, {
      onSuccess: () => {
        logSuccess(AdvertisementsContent.Delete.Notification.SUCCESS);
        closeDeleteModal();
        setSelectedAdId(null);
      },
    });
  };

  return (
    <Space
      direction="vertical"
      size="large"
      justify="start"
      align="start"
      fullWidth
    >
      <Space
        direction="horizontal"
        size="medium"
        justify="between"
        align="center"
        fullWidth
      >
        <div />
        <Button
          onClick={openAddModal}
          className="nb-page-admin-advertisements-add-button"
        >
          {AdvertisementsContent.Button.ADD}
        </Button>
      </Space>
      <Divider />
      {isEmpty && !isFetching ? (
        <Empty text={AdvertisementsContent.Empty} />
      ) : (
        <div className="nb-page-admin-advertisements-list">
          {advertisements.map((ad) => (
            <div key={ad.id} className="nb-page-admin-advertisements-item">
              {ad.imageUrl && (
                <img
                  src={ad.imageUrl}
                  alt="Оголошення"
                  className="nb-page-admin-advertisements-item-img"
                />
              )}
              <div className="nb-page-admin-advertisements-item-info">
                {ad.url && (
                  <Paragraph variant={2}>
                    <a
                      href={ad.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nb-page-admin-advertisements-item-link"
                    >
                      {ad.url}
                    </a>
                  </Paragraph>
                )}
                <Paragraph variant={2}>
                  {ad.targetRole
                    ? TARGET_ROLE_LABEL[ad.targetRole]
                    : AdvertisementsContent.Add.TargetRole.ALL}
                </Paragraph>
              </div>
              <Button
                variant="secondary"
                type="button"
                onClick={() => deleteClickHandler(ad)}
              >
                {AdvertisementsContent.Button.DELETE}
              </Button>
            </div>
          ))}
        </div>
      )}

      <ContentModal
        portalId="add-advertisement-modal"
        footerId="add-advertisement-footer"
        open={isAddModalOpen}
        title={AdvertisementsContent.Add.TITLE}
        emoji={AdvertisementsContent.Add.EMOJI}
        onClose={closeAddModalHandler}
      >
        <Space
          direction="vertical"
          size="medium"
          justify="start"
          align="start"
          fullWidth
        >
          <Button
            variant="secondary"
            type="button"
            fullWidth
            onClick={openImageModal}
          >
            {uploadedImage
              ? AdvertisementsContent.Add.Image.CHANGE
              : AdvertisementsContent.Add.Image.SELECT}
          </Button>
          {uploadedImage && (
            <img
              src={uploadedImage}
              alt="Preview"
              className="nb-page-admin-advertisements-preview-img"
            />
          )}
          <label
            className="nb-page-admin-advertisements-url-label"
            htmlFor="ad-url-input"
          >
            {AdvertisementsContent.Add.Url.LABEL}
          </label>
          <input
            id="ad-url-input"
            type="url"
            className="nb-page-admin-advertisements-url-input"
            placeholder={AdvertisementsContent.Add.Url.PLACEHOLDER}
            value={adUrl}
            onChange={(e) => setAdUrl(e.target.value)}
          />
          <span className="nb-page-admin-advertisements-url-label">
            {AdvertisementsContent.Add.TargetRole.LABEL}
          </span>
          <div className="nb-page-admin-advertisements-role-options">
            {TARGET_ROLE_OPTIONS.map((option) => (
              <label
                key={String(option.value)}
                className="nb-page-admin-advertisements-role-option"
              >
                <input
                  type="radio"
                  name="ad-target-role"
                  value={String(option.value)}
                  checked={targetRole === option.value}
                  onChange={() => setTargetRole(option.value)}
                  className="nb-page-admin-advertisements-role-radio"
                />
                {option.label}
              </label>
            ))}
          </div>
          <Button
            variant="primary"
            type="button"
            fullWidth
            onClick={addSubmitHandler}
            disabled={!uploadedImage || isCreating}
          >
            {AdvertisementsContent.Button.ADD}
          </Button>
        </Space>
      </ContentModal>

      <ImageUploadModal
        open={isImageModalOpen}
        outputImageType="image/webp"
        aspect={9 / 16}
        zoomSpeed={0.5}
        cropShape="rect"
        id="advertisement-upload-input"
        name="advertisement-upload-input"
        portalId="advertisement-image-upload-modal"
        title={AdvertisementsContent.Add.Image.MODAL_TITLE}
        emoji={AdvertisementsContent.Add.EMOJI}
        selectButtonText={AdvertisementsContent.Add.Image.SELECT}
        uploadButtonText={AdvertisementsContent.Add.Image.UPLOAD}
        inputLabel={AdvertisementsContent.Add.Image.LABEL}
        withImageCropper
        withUploadButton
        onClose={closeImageModal}
        onUpload={imageUploadHandler}
      />

      <ActionModal
        text={AdvertisementsContent.Delete.TITLE}
        emoji={AdvertisementsContent.Delete.EMOJI}
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onSubmit={deleteSubmitHandler}
      />
    </Space>
  );
};
